// V2-T4 : SettingsSubNav retiré du layout parent.
// V4-T11 : sidebar sticky — wrapper desktop reçoit sticky top-0 h-screen overflow-y-auto
// V4-T13 : scroll débloqué — outer div h-screen overflow-hidden + main overflow-y-auto
import React, { useState, useEffect } from "react";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import BottomNav from "./BottomNav";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "../ui/sheet";
import { useAuth } from "../../contexts/AuthContext";
import useUserNotificationsRealtime from "../../hooks/useUserNotificationsRealtime";
import {
  dismissPunch,
  ensureMemoQueueAutoFlush,
  ensurePunchQueueAutoFlush,
  flushPunchQueue,
  getPunchQueue,
  isPunchQueueSyncing,
  setPunchQueueOwner,
  subscribePunchQueue,
} from "../../lib/offlineQueue";

function useOnlineStatus() {
  const [offline, setOffline] = useState(() =>
    typeof navigator === "undefined" ? false : !navigator.onLine
  );
  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);
  return offline;
}

function OfflineBanner({ offline }) {
  if (!offline) return null;
  return (
    <div
      data-testid="offline-indicator"
      role="status"
      className="w-full bg-amber-500 text-white text-xs font-semibold text-center py-1.5 px-3"
    >
      {/* Le pointage n'est plus indisponible : il est mis en file locale et
          renvoyé au retour du réseau. Le bandeau doit dire la vérité, sinon le
          compagnon renonce à pointer et l'heure est perdue pour de bon. */}
      Hors ligne — vos pointages sont enregistrés sur l'appareil et partiront au retour du réseau
    </div>
  );
}

/**
 * Compteur des pointages encore sur le téléphone. Le compagnon doit voir
 * combien il en reste, et surtout que rien n'est perdu : c'est la seule preuve
 * qu'il a que sa journée sera payée.
 */
function PunchQueueBanner({ offline }) {
  const { user } = useAuth();
  const userId = user?.id || user?._id || null;
  const [items, setItems] = useState([]);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (!userId) { setItems([]); return undefined; }
    setPunchQueueOwner(userId);
    const refresh = () => {
      setItems(getPunchQueue(userId));
      setSyncing(isPunchQueueSyncing());
    };
    const unsubscribe = subscribePunchQueue(refresh);
    ensurePunchQueueAutoFlush();
    // Les mémos hors ligne se rejouaient UNIQUEMENT si l'utilisateur rouvrait
    // l'écran Mémos : après un redémarrage à froid de la PWA, la file dormait
    // indéfiniment. Le shell est monté sur tous les écrans, comme pour le
    // pointage — les écouteurs réseau y vivent maintenant aussi.
    ensureMemoQueueAutoFlush();
    refresh();
    return unsubscribe;
  }, [userId]);

  const pending = items.filter((it) => it.status !== "blocked");
  const blocked = items.filter((it) => it.status === "blocked");
  if (pending.length === 0 && blocked.length === 0) return null;

  return (
    <div data-testid="punch-queue-indicator" role="status" className="w-full text-xs">
      {pending.length > 0 && (
        <div className="w-full bg-sky-700 text-white px-3 py-1.5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
          <span className="font-semibold text-center">
            {syncing
              ? "Envoi des pointages en cours…"
              : `${pending.length} pointage${pending.length > 1 ? "s" : ""} en attente d'envoi — rien n'est perdu`}
          </span>
          {!offline && (
            <button
              type="button"
              data-testid="punch-queue-sync-btn"
              onClick={() => flushPunchQueue({ userId, force: true })}
              disabled={syncing}
              className="shrink-0 rounded-md bg-white/20 px-2 py-0.5 font-semibold disabled:opacity-50"
            >
              Envoyer maintenant
            </button>
          )}
        </div>
      )}
      {blocked.map((it) => (
        <div
          key={it.id}
          data-testid={`punch-queue-blocked-${it.id}`}
          className="w-full bg-red-700 text-white px-3 py-1.5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1"
        >
          <span className="text-center">
            {it.site_name ? `${it.site_name} — ` : ""}{it.error}
          </span>
          <button
            type="button"
            data-testid={`punch-queue-dismiss-${it.id}`}
            onClick={() => dismissPunch(it.id)}
            className="shrink-0 rounded-md bg-white/20 px-2 py-0.5 font-semibold"
          >
            J'ai compris
          </button>
        </div>
      ))}
    </div>
  );
}

export default function AppLayout() {
  const { user, company } = useAuth();
  const { pathname } = useLocation();
  const offline = useOnlineStatus();
  // Écrans tâche plein écran (wizard création + détail) : ils ont leur propre
  // barre d'action fixe en bas — la BottomNav la masquerait (cf. design fix).
  const isFullscreenTask = /^\/sites\/[^/]+\/tasks\/.+/.test(pathname);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => typeof window !== "undefined" && window.localStorage.getItem("nav-collapsed") === "1"
  );
  const toggleCollapse = () =>
    setCollapsed((c) => {
      const next = !c;
      try { window.localStorage.setItem("nav-collapsed", next ? "1" : "0"); } catch { /* quota */ }
      return next;
    });
  useUserNotificationsRealtime(!!user);

  if (user && user.is_onboarded === false) {
    if (user.role === "owner") return <Navigate to="/onboarding" replace />;
    // BTP-Suivi : un sous-traitant fraîchement onboardé (mot de passe créé)
    // arrive directement sur son espace dédié (cf. PR #212-#213).
    if (user.role === "soustraitant") return <Navigate to="/sous-traitant" replace />;
    return <Navigate to="/welcome" replace />;
  }

  // BTP-Suivi : un sous-traitant connecté qui atterrit sur le shell
  // ChantierFlow (sidebar/dashboard ne lui correspond pas) est redirigé
  // vers son espace dédié.
  if (user && user.role === "soustraitant" && typeof window !== "undefined") {
    const path = window.location.pathname;
    const allowedPrefixes = ["/sous-traitant", "/settings/account", "/logout"];
    if (!allowedPrefixes.some((p) => path.startsWith(p))) {
      return <Navigate to="/sous-traitant" replace />;
    }
  }

  return (
    /*
     * V4-T13 : passage de 'min-h-screen flex' à 'h-screen flex overflow-hidden'.
     * L'axe de scroll est désormais contrôlé exclusivement par le <main
     * overflow-y-auto>. Cela évite le conflit où la page ne scrollait pas
     * sur /settings/payroll-variables (et autres pages longues).
     */
    <div
      className="h-screen flex overflow-hidden"
      style={{ backgroundColor: 'var(--cf-bg)' }}
    >
      <a
        href="#app-main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[70] focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-primary-foreground focus:shadow-lg"
        data-testid="skip-to-content"
      >
        Aller au contenu
      </a>

      {/*
       * V4-T11 : sticky top-0 h-screen overflow-y-auto self-start.
       * La sidebar reste en place pendant le scroll du contenu principal.
       * overflow-y-auto permet son propre scroll si la liste nav est longue.
       */}
      <div className="hidden md:block sticky top-0 h-screen overflow-y-auto self-start shrink-0">
        <Sidebar user={user} company={company} collapsed={collapsed} onToggleCollapse={toggleCollapse} />
      </div>

      {/* Sidebar mobile */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-[260px]" data-testid="mobile-sidebar">
          <SheetTitle className="sr-only">Menu de navigation</SheetTitle>
          <SheetDescription className="sr-only">
            Accès aux différentes sections de KNOWROK.
          </SheetDescription>
          <Sidebar
            user={user}
            company={company}
            onNavigate={() => setMobileOpen(false)}
          />
        </SheetContent>
      </Sheet>

      {/* Colonne contenu : overflow-y-auto pour le scroll de page */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <OfflineBanner offline={offline} />
        <PunchQueueBanner offline={offline} />
        <Topbar onOpenSidebar={() => setMobileOpen(true)} />
        {/*
          V4-T13 : main overflow-y-auto — seul élément qui scrolle.
          flex-1 làissé pour que la zone principale prenne le reste
          de la hauteur sous le Topbar.
        */}
        <main
          data-testid="app-main"
          id="app-main"
          className="flex-1 overflow-y-auto"
          tabIndex={-1}
        >
          {/* Écrans tâche plein écran : conteneur neutre (pas de max-width, de
              padding, ni d'animation translateY — ce transform casserait le
              position:fixed de leur barre d'action en bas). */}
          <div
            className={isFullscreenTask ? "" : "max-w-7xl mx-auto p-4 md:p-6 pb-24 md:pb-6 animate-fade-in-up"}
            style={{ backgroundColor: 'var(--cf-bg)' }}
          >
            <Outlet />
          </div>
        </main>
      </div>

      {/* Barre de navigation mobile (etape B) — masquee en desktop et sur les
          écrans tâche plein écran (qui ont leur propre barre d'action). */}
      {!isFullscreenTask && <BottomNav user={user} onOpenMenu={() => setMobileOpen(true)} />}
    </div>
  );
}
